import { useRef, useState, useEffect, useCallback } from "react";
import { Globe, ChevronLeft, ChevronRight } from "lucide-react";
import { Flag } from "./Flag";

function CountryChip({ active, onClick, country, flag, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-heading font-bold uppercase tracking-wide border ${
        active
          ? "bg-[#E5B842] text-black border-[#E5B842]"
          : "bg-[#161616] text-slate-300 border-[rgba(212,175,55,0.22)] hover:border-[#E5B842]/60 hover:text-[#E5B842]"
      }`}
      style={{ transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease" }}
    >
      {country ? <Flag country={country} width={18} /> : <Globe className="w-3.5 h-3.5" />}
      <span>{label}</span>
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active ? "bg-black/20 text-black" : "bg-[#0a0a0a] text-slate-400"}`}>{count}</span>
    </button>
  );
}

const ARROW = "flex absolute top-1/2 -translate-y-1/2 z-10 items-center justify-center w-9 h-9 rounded-full bg-[#0f0f0f] border border-[rgba(212,175,55,0.3)] text-slate-300 hover:text-[#E5B842] hover:border-[#E5B842]/60 disabled:opacity-30 disabled:cursor-not-allowed shadow-lg";

export function CountryFilterRow({ countries, selected, totalAll, onSelect, testid = "country-filter-row" }) {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setOverflow(el.scrollWidth > el.clientWidth + 1);
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [update, countries]);

  const scrollDir = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.65, behavior: "smooth" });
  };

  if (!countries || countries.length === 0) return null;

  return (
    <div className="mt-4 relative" data-testid={testid}>
      {overflow && (
        <button onClick={() => scrollDir(-1)} disabled={atStart} aria-label="Scroll countries left" data-testid="country-scroll-left"
          className={`${ARROW} left-0 hidden sm:flex`} style={{ transition: "color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease" }}>
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      <div ref={ref} className={`overflow-x-auto scrollbar-none ${overflow ? "sm:px-12" : ""}`}>
        <div className="flex items-center gap-2 w-max pb-1">
          <CountryChip active={selected === "ALL"} onClick={() => onSelect("ALL")} country={null} label="All" count={totalAll} testid="country-chip-ALL" />
          {countries.map((c) => (
            <CountryChip key={c.code} active={selected === c.code} onClick={() => onSelect(c.code)} country={c} label={c.name} count={c.count} testid={`country-chip-${c.code}`} />
          ))}
        </div>
      </div>
      {overflow && (
        <button onClick={() => scrollDir(1)} disabled={atEnd} aria-label="Scroll countries right" data-testid="country-scroll-right"
          className={`${ARROW} right-0 hidden sm:flex`} style={{ transition: "color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease" }}>
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
